
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

/**
 * Отвязка платежного метода курьера
 * @param courierId - ID курьера
 * @returns Результат операции
 */
export const unlinkPaymentMethod = async (courierId: string) => {
  try {
    console.log(`Отвязка платежного метода для курьера ${courierId}`);

    // Обновляем информацию о курьере, очищая платежные данные
    const { data, error } = await supabase
      .from('couriers')
      .update({
        payment_method_id: null,
        card_last4: null,
        card_type: null,
        card_expiry_month: null,
        card_expiry_year: null,
        payment_updated_at: new Date().toISOString()
      })
      .eq('id', courierId);

    if (error) {
      console.error('Ошибка при отвязке платежного метода:', error);
      return { success: false, error };
    }

    // Отмечаем все связанные подписки как требующие обновления платежного метода
    // Это не отменяет подписки, но помечает их для обновления
    // Исправляем ошибку типа, используя функциональный подход вместо rpc для вызова
    
    // Сначала получаем ID аренд, принадлежащих этому курьеру
    const { data: rentalsData, error: rentalsError } = await supabase
      .from('rentals')
      .select('id')
      .eq('courier_id', courierId);
      
    if (rentalsError) {
      console.error('Ошибка при получении аренд курьера:', rentalsError);
      // Продолжаем выполнение, так как основная операция удаления карты уже прошла успешно
    } 
    
    if (rentalsData && rentalsData.length > 0) {
      // Получаем массив ID аренд
      const rentalIds = rentalsData.map(rental => rental.id);
      
      // Обновляем статусы подписок для этих аренд
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .update({
          status: 'payment_update_needed',
          previous_status: 'active'
        })
        .eq('status', 'active')
        .not('payment_method_id', 'is', null)
        .in('rental_id', rentalIds);

      if (subscriptionError) {
        console.error('Предупреждение: не удалось обновить статус подписок:', subscriptionError);
        // Продолжаем выполнение, так как основная операция удаления выполнена успешно
      }
    }

    console.log('Платежный метод успешно отвязан');
    return { success: true, data };
  } catch (error: any) {
    console.error('Непредвиденная ошибка при отвязке платежного метода:', error);
    return { success: false, error };
  }
};

/**
 * Получение информации о платежном методе курьера
 * @param courierId - ID курьера
 * @returns Данные платежного метода
 */
export const getPaymentMethodInfo = async (courierId: string) => {
  try {
    // Получаем информацию о платежном методе из данных курьера
    const { data, error } = await supabase
      .from('couriers')
      .select('payment_method_id, card_last4, card_type, card_expiry_month, card_expiry_year, payment_updated_at')
      .eq('id', courierId)
      .single();

    if (error) {
      console.error('Ошибка при получении платежного метода:', error);
      return { success: false, error, data: null };
    }

    // Если платежного метода нет, вернем пустой объект
    if (!data.payment_method_id) {
      return { success: true, data: null };
    }

    return { 
      success: true, 
      data: {
        paymentMethodId: data.payment_method_id,
        cardLast4: data.card_last4,
        cardType: data.card_type,
        expiryMonth: data.card_expiry_month,
        expiryYear: data.card_expiry_year,
        updatedAt: data.payment_updated_at
      }
    };
  } catch (error: any) {
    console.error('Непредвиденная ошибка при получении информации о платежном методе:', error);
    return { success: false, error, data: null };
  }
};
